import { useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, FileText, Pencil } from 'lucide-react';
import { api, ApiError, errorMessage } from '@/lib/api';
import { useDocuments } from '@/lib/documents';
import type { Program } from '@/lib/types';
import { cn, fmtDateTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Textarea } from '@/components/ui/field';
import { Alert, Badge, Card, PageHeader, Spinner } from '@/components/ui/misc';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';

export type CriterionStatus = 'NOT_YET_MEASURABLE' | 'ON_TRACK' | 'AT_RISK' | 'ACHIEVED';
export interface Criterion {
  id: string;
  group: string;
  name: string;
  target: string;
  measurementMethod: string | null;
  measurableFrom: { number: number; name: string; started: boolean } | null;
  measurableNote: string | null;
  status: CriterionStatus;
  currentValue: string | null;
  evidence: { id: string; title: string } | null;
  updatedAt: string;
}
interface HistoryRow {
  at: string;
  actor: string;
  note: string | null;
  earlyReason: string | null;
  changes: Array<{ field: string; from: unknown; to: unknown }>;
}

export const CRITERION_STATUS = {
  NOT_YET_MEASURABLE: { label: 'Not yet measurable', tone: 'neutral' },
  ON_TRACK: { label: 'On track', tone: 'ok' },
  AT_RISK: { label: 'At risk', tone: 'warn' },
  ACHIEVED: { label: 'Achieved', tone: 'ok' },
} as const;
const FIELD: Record<string, string> = { status: 'Status', currentValue: 'Value', evidenceDocumentId: 'Evidence' };

export const criteriaKey = (programId: string) => ['criteria', programId] as const;

export function CriteriaPage() {
  const program = useOutletContext<Program>();
  const [params] = useSearchParams();
  const focus = params.get('c');
  const canEdit = program.capabilities.includes('plan.manage');
  const q = useQuery({
    queryKey: criteriaKey(program.id),
    queryFn: () => api.get<Criterion[]>(`/api/programs/${program.id}/criteria`),
  });
  const [editing, setEditing] = useState<Criterion | null>(null);
  if (!q.data) return <Spinner className="h-64" />;

  const groups = [...new Set(q.data.map((c) => c.group))];
  const pending = q.data.filter((c) => c.status === 'NOT_YET_MEASURABLE').length;
  const counts = (Object.keys(CRITERION_STATUS) as CriterionStatus[]).map((s) => ({
    s,
    n: q.data.filter((c) => c.status === s).length,
  }));

  return (
    <div className="mx-auto max-w-[1100px] px-6 py-6">
      <PageHeader
        title="Success Criteria"
        description={`The PA §5 targets. Most become measurable from the pilot in Phase 6; until then they say so plainly.`}
      />
      <div className="mb-5 flex flex-wrap gap-2">
        {counts.map(({ s, n }) => (
          <Badge key={s} tone={CRITERION_STATUS[s].tone} className="px-2.5 py-1 text-xs">
            {n} {CRITERION_STATUS[s].label.toLowerCase()}
          </Badge>
        ))}
        {pending === q.data.length && (
          <span className="text-xs text-muted">None of the {q.data.length} criteria can be measured yet.</span>
        )}
      </div>
      <div className="space-y-5">
        {groups.map((g) => (
          <section key={g}>
            <h2 className="mb-2 text-[15px] font-semibold">{g}</h2>
            <Card className="divide-y divide-border p-0">
              {q.data
                .filter((c) => c.group === g)
                .map((c) => (
                  <CriterionRow
                    key={c.id}
                    program={program}
                    c={c}
                    canEdit={canEdit}
                    highlight={c.id === focus}
                    onEdit={() => setEditing(c)}
                  />
                ))}
            </Card>
          </section>
        ))}
      </div>
      {editing && <EditCriterion program={program} c={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function CriterionRow({
  program,
  c,
  canEdit,
  highlight,
  onEdit,
}: {
  program: Program;
  c: Criterion;
  canEdit: boolean;
  highlight: boolean;
  onEdit: () => void;
}) {
  const [open, setOpen] = useState(highlight);
  const history = useQuery({
    queryKey: [...criteriaKey(program.id), 'history', c.id],
    queryFn: () => api.get<HistoryRow[]>(`/api/programs/${program.id}/criteria/${c.id}/history`),
    enabled: open,
  });

  return (
    <div id={`c-${c.id}`} className={cn('px-4 py-3', highlight && 'bg-brand-soft/40')}>
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        <div className="min-w-[240px] flex-1">
          <p className="text-sm font-medium">{c.name}</p>
          <p className="text-xs text-muted">
            Target: {c.target}
            {c.measurementMethod ? ` · ${c.measurementMethod}` : ''}
          </p>
        </div>
        <div className="w-40 text-xs text-muted">
          {c.measurableFrom
            ? `From Phase ${c.measurableFrom.number}${c.measurableFrom.started ? ' (started)' : ''}`
            : 'Measurable now'}
          {c.measurableNote ? <div>{c.measurableNote}</div> : null}
        </div>
        <div className="flex w-56 flex-wrap items-center gap-2">
          <Badge tone={CRITERION_STATUS[c.status].tone}>{CRITERION_STATUS[c.status].label}</Badge>
          {c.currentValue && <span className="text-sm font-semibold tabular-nums">{c.currentValue}</span>}
        </div>
        <div className="flex items-center gap-1">
          {c.evidence && (
            <Link
              to={`/p/${program.id}/documents/${c.evidence.id}`}
              title={c.evidence.title}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[13px] text-brand-text hover:bg-surface-2"
            >
              <FileText className="size-4" /> Evidence
            </Link>
          )}
          {canEdit && (
            <Button variant="ghost" size="icon" aria-label={`Update ${c.name}`} onClick={onEdit}>
              <Pencil />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            aria-label={open ? 'Hide history' : 'Show history'}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            <ChevronDown className={cn('transition', open && 'rotate-180')} />
          </Button>
        </div>
      </div>
      {open && (
        <div className="mt-2 border-l-2 border-border pl-3 text-xs">
          {!history.data ? (
            <Spinner className="h-8" />
          ) : !history.data.length ? (
            <p className="text-muted">No changes yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {history.data.map((h, i) => (
                <li key={i}>
                  <span className="text-muted">
                    {fmtDateTime(h.at)} · {h.actor}
                  </span>
                  {h.changes.length > 0 && (
                    <span className="text-muted"> · {h.changes.map((x) => FIELD[x.field] ?? x.field).join(', ')}</span>
                  )}
                  {h.note && <p>{h.note}</p>}
                  {h.earlyReason && <p className="text-muted">Measured early: {h.earlyReason}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function EditCriterion({ program, c, onClose }: { program: Program; c: Criterion; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const docs = useDocuments(program.id);
  const [f, setF] = useState({
    status: c.status,
    currentValue: c.currentValue ?? '',
    evidenceDocumentId: c.evidence?.id ?? '',
    note: '',
    earlyReason: '',
  });
  const [needsReason, setNeedsReason] = useState(
    c.status === 'NOT_YET_MEASURABLE' && !!c.measurableFrom && !c.measurableFrom.started,
  );
  const leavingEarly = needsReason && f.status !== 'NOT_YET_MEASURABLE';
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/programs/${program.id}/criteria/${c.id}`, {
        status: f.status,
        currentValue: f.status === 'NOT_YET_MEASURABLE' ? null : f.currentValue || null,
        evidenceDocumentId: f.evidenceDocumentId || null,
        note: f.note,
        ...(leavingEarly ? { earlyReason: f.earlyReason } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: criteriaKey(program.id) });
      void qc.invalidateQueries({ queryKey: ['executive', program.id] });
      toast.ok('Criterion updated.');
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && (e.details as { needsReason?: boolean } | undefined)?.needsReason)
        setNeedsReason(true);
      toast.error(errorMessage(e));
    },
  });
  const visibleDocs = docs.data?.filter((d) => !d.internal) ?? [];

  return (
    <Dialog
      open
      onClose={onClose}
      title={c.name}
      description={`Target: ${c.target}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={f.note.trim().length < 5 || (leavingEarly && f.earlyReason.trim().length < 10)}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Status" htmlFor="cr-status">
            <Select
              id="cr-status"
              value={f.status}
              onChange={(e) => setF({ ...f, status: e.target.value as CriterionStatus })}
            >
              {(Object.keys(CRITERION_STATUS) as CriterionStatus[]).map((s) => (
                <option key={s} value={s}>
                  {CRITERION_STATUS[s].label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Current value" htmlFor="cr-value" hint="Aggregate only, such as 98.2%.">
            <Input
              id="cr-value"
              value={f.currentValue}
              disabled={f.status === 'NOT_YET_MEASURABLE'}
              maxLength={120}
              onChange={(e) => setF({ ...f, currentValue: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Evidence" htmlFor="cr-evidence" hint="A document the committee can see.">
          <Select
            id="cr-evidence"
            value={f.evidenceDocumentId}
            onChange={(e) => setF({ ...f, evidenceDocumentId: e.target.value })}
          >
            <option value="">None</option>
            {visibleDocs.map((d) => (
              <option key={d.id} value={d.id}>
                {d.title}
              </option>
            ))}
          </Select>
        </Field>
        {leavingEarly && (
          <>
            <Alert tone="warn">
              This criterion becomes measurable from Phase {c.measurableFrom?.number}, which has not started.
            </Alert>
            <Field label="Why measure it now?" htmlFor="cr-early">
              <Textarea
                id="cr-early"
                rows={2}
                value={f.earlyReason}
                onChange={(e) => setF({ ...f, earlyReason: e.target.value })}
              />
            </Field>
          </>
        )}
        <Field label="Note" htmlFor="cr-note" hint="Kept in the criterion's history.">
          <Textarea id="cr-note" rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </Field>
      </div>
    </Dialog>
  );
}
